import React, { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Banknote, Eye, Lock, FileSignature, Download } from "lucide-react";
import { toast } from "sonner";

const REVEAL_TIMEOUT_MS = 30000;

/**
 * Phase F2 — Admin Reveal panel for an ACH bank-info entry on a contract
 * document. Decrypts via /admin/contracts/{id}/ach-bank-info/reveal which
 * also logs an `ach_reveal` event in the audit trail.
 *
 * Phase F-trust follow-up (Task 4) — also exposes View / Download buttons
 * for the per-document SIGNED ACH PDF when the customer has digitally
 * signed the ACH authorization leg. Mirrors ``CustomerContractDocsList``
 * on the customer dashboard but on the admin side. Streams via
 * ``GET /admin/contracts/{id}/pdf?variant=signed&doc_id=<ach_doc>``.
 *
 * Props:
 *   contractId, doc (the document object including ach_bank_info)
 */
export function AdminAchBankInfoCard({ contractId, doc }) {
    const [revealed, setRevealed] = useState(null);
    const [busy, setBusy] = useState(false);
    const [signedBusy, setSignedBusy] = useState(null);  // "view" | "download" | null
    const bi = doc?.ach_bank_info;

    // Auto-mask after 30 seconds
    useEffect(() => {
        if (!revealed) return;
        const t = setTimeout(() => setRevealed(null), REVEAL_TIMEOUT_MS);
        return () => clearTimeout(t);
    }, [revealed]);

    if (!bi) return null;

    const onReveal = async () => {
        setBusy(true);
        try {
            const { data } = await api.get(`/admin/contracts/${contractId}/ach-bank-info/reveal`, {
                params: { doc_id: doc.doc_id },
            });
            setRevealed(data);
            toast.success("Audit event logged.");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    // Phase Task 4 — fetch the signed ACH PDF as a blob, so we can read
    // the integrity header before opening or saving. ``action`` is "view"
    // (opens in a new tab) or "download" (saves to disk).
    const fetchSignedAchBlob = async () => {
        const resp = await api.get(`/admin/contracts/${contractId}/pdf`, {
            params: { variant: "signed", doc_id: doc.doc_id },
            responseType: "blob",
        });
        const integrity = resp.headers?.["x-rentflow-integrity"];
        if (integrity === "tampered") {
            toast.warning("Integrity warning: this signed PDF doesn't match the recorded hash. Audit logged.");
        }
        return new Blob([resp.data], { type: "application/pdf" });
    };

    const onViewSigned = async () => {
        setSignedBusy("view");
        let url = null;
        try {
            const blob = await fetchSignedAchBlob();
            url = URL.createObjectURL(blob);
            // window.open returns null when popup blockers fire; fall back to navigating.
            const win = window.open(url, "_blank", "noopener");
            if (!win) {
                window.location.href = url;
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            if (url) setTimeout(() => URL.revokeObjectURL(url), 60_000);
            setSignedBusy(null);
        }
    };

    const onDownloadSigned = async () => {
        setSignedBusy("download");
        try {
            const blob = await fetchSignedAchBlob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `ach-authorization-signed-${doc.doc_id || "doc"}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 5000);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSignedBusy(null);
        }
    };

    const hasSigned = Boolean(doc?.has_signed_pdf);
    const notSignedTip = "ACH authorization not signed yet";

    return (
        <Card data-testid="admin-ach-bank-info-card">
            <CardContent className="p-5 space-y-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <p className="text-xs font-medium tracking-[0.18em] uppercase text-slate-500 flex items-center gap-1.5">
                            <Banknote size={12} /> ACH BANK INFORMATION
                        </p>
                        <h2 className="font-display text-lg font-semibold text-slate-900 mt-1">
                            {bi.bank_name} · <span className="capitalize">{bi.account_type}</span> ··•• {bi.account_number_last4}
                        </h2>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Account holder: <span className="text-slate-800">{bi.account_holder_name || "—"}</span>
                        </p>
                    </div>
                    {!revealed && (
                        <Button onClick={onReveal} disabled={busy} variant="outline"
                            className="gap-1.5" data-testid="ach-reveal-btn">
                            <Eye size={14} /> {busy ? "Revealing…" : "Reveal full numbers"}
                        </Button>
                    )}
                    {revealed && (
                        <Button onClick={() => setRevealed(null)} variant="outline" className="gap-1.5"
                            data-testid="ach-mask-btn">
                            <Lock size={14} /> Mask
                        </Button>
                    )}
                </div>

                {revealed && (
                    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-1 text-sm font-mono"
                        data-testid="ach-reveal-panel">
                        <p><span className="text-amber-900 not-italic font-sans text-xs uppercase tracking-wider mr-2">Routing</span><span data-testid="ach-reveal-routing">{revealed.routing_number}</span></p>
                        <p><span className="text-amber-900 not-italic font-sans text-xs uppercase tracking-wider mr-2">Account</span><span data-testid="ach-reveal-account">{revealed.account_number}</span></p>
                        <p className="text-[11px] text-amber-800 font-sans not-italic mt-1.5">
                            Visible for 30 seconds. Every reveal is recorded in this contract&apos;s audit trail with your user, IP and timestamp.
                        </p>
                    </div>
                )}

                {!revealed && (
                    <p className="text-xs text-slate-500 italic">
                        Routing &amp; full account number are encrypted at rest with AES-256-GCM. Reveals are audit-logged.
                    </p>
                )}

                {/* Phase Task 4 — signed-ACH PDF affordance. Mirrors the
                    customer-side per-doc download row. Hidden entirely
                    when the contract has no documents[] entry for the
                    ACH leg (defensive — AdminAchBankInfoCard is only
                    rendered when doc.ach_bank_info exists, but the
                    signed PDF is a separate artifact). */}
                <div className="border-t border-slate-100 pt-3 flex items-center justify-between gap-2 flex-wrap"
                    data-testid="ach-signed-pdf-row">
                    <div className="flex items-center gap-2 text-xs text-slate-700">
                        <FileSignature size={14} className="text-slate-500" />
                        {hasSigned ? (
                            <span data-testid="ach-signed-status">
                                Digitally signed ACH authorization on file.
                            </span>
                        ) : (
                            <span data-testid="ach-signed-status" className="text-slate-500">
                                ACH authorization not signed yet.
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={onViewSigned}
                            disabled={!hasSigned || signedBusy !== null}
                            title={hasSigned ? "Open the signed ACH PDF in a new tab" : notSignedTip}
                            data-testid="ach-view-signed-btn"
                            className="gap-1.5"
                        >
                            <Eye size={13} /> {signedBusy === "view" ? "Opening…" : "View signed ACH"}
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={onDownloadSigned}
                            disabled={!hasSigned || signedBusy !== null}
                            title={hasSigned ? "Download the signed ACH PDF" : notSignedTip}
                            data-testid="ach-download-signed-btn"
                            className="gap-1.5"
                        >
                            <Download size={13} /> {signedBusy === "download" ? "…" : "Download"}
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

export default AdminAchBankInfoCard;
